/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { ColumnDef } from '@tanstack/react-table';
import { TriageAgentHistoryLine, TriageResultConclusionItem } from 'triage-agent/types';
import { z } from 'zod';

import { OUTPUT, RESULT, Step, WebAgentActionName } from './data';
import { RunAttempt, RunMessage, RunResult, RunStatus, Story, SuiteExecution, TriggerUser } from './protocols';
// Add JSON support to zod

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalSchema>;
type Json = Literal | { [key: string]: Json } | Json[];
const jsonSchema: z.ZodType<Json> = z.lazy(() => z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)]));

export interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
}

// Pagination from knex-paginate

export interface IWithPagination<Data> {
    data: Data[];
    pagination: IPagination;
}

export type IPagination = {
    /** Note: page numbers start at 1. */
    currentPage: number;
    perPage: number;
    from: number;
    to: number;
    total: number;
    lastPage: number;
    prevPage: number | null;
    nextPage: number | null;
};

// User
export interface UserData {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    isSuperUser?: boolean;
    impersonatedBy?: number;
    createdAt: Date;
    updatedAt: Date;
}

export const ROLES = {
    ADMIN: 'admin',
    MEMBER: 'member',
    OPERATOR: 'operator',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export interface UserDataWithRole extends UserData {
    role: Role;
    operatorOrganisations?: number[];
}

// Invitation
export type InvitationStatus = 'pending' | 'accepted' | 'expired';

export type InvitationData = {
    id: number;
    orgId: number;
    email: string;
    role: string;
    status: InvitationStatus;
    createdAt: Date;
    expiresAt: Date;
};

// Organisation

export type OrganisationData = {
    id: number;
    name: string;
    slackWebhookUrl: string;
    discordWebhookUrl: string;
};

// Story configuration

export interface Viewport {
    width: number;
    height: number;
}

// Run and Execution

export type SuiteExecutionTrigger = {
    execution: {
        id: number;
        parentId: number | null;
        testSuiteId: number;
        systemTriggered: boolean;
        triggerUserId: number;
        runLen: number;
    };
    runMessages: RunMessage[];
};

export type RunViewerData = {
    run: {
        id: number;
        storyId: number;
        suiteExecutionId: number;
        status: RunStatus;
        result: RESULT | null;
        output: OUTPUT | null;
        agentCommitHash?: string;
        createdAt: string | null;
        steps: Step[];
        entryHref: string;
        sessionUuid: string | null;
        variables: Record<string, string>;
        videoTimeOffset: number;
    };
    story: Story;
    blocks: Story[];
    result: RunResult | null;
    runAttempt: RunAttempt | null;
    attemptIndexes: number[];
    triggerUser: TriggerUser;
    storyInstructions: {
        instructions: string;
        revision: number;
        id: number;
        storySlug: string;
        createdAt: string;
    };
    triage: {
        history: TriageAgentHistoryLine[];
        conclusion: TriageResultConclusionItem[];
    };
};

export type StartExecutionResponse = {
    execution: SuiteExecution;
    runMessages: RunMessage[];
};

// For listing all runs of story

export type StoryRunsList = {
    id: number;
    createdAt: string;
    test_suite_execution_id: number | null;
    status: RunStatus;
    result: RESULT;
    agent_commit_hash?: string | null;
    storyId: number;
    storyCreatedAt: string;
    entryHref: string;
    goal: string;
    steps: Step[];
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
}[];

// Live agent

export type LiveSession = {
    session: string;
    runId: string;
};

export type SessionStatus = 'sessionEnded' | 'sessionReady';

export const DEFAULT_BROWSER = 'chromium';
export const DEFAULT_VIEWPORT = {
    width: 1280,
    height: 720,
};

const priorityEnum = z.enum(['P0', 'P1', 'P2']);
// The priority of the action to be taken (e.g., P0 for highest priority, P2 for lower priority)

export const eachFeedbackSchema = z.object({
    priority: priorityEnum,
    issue: z.string(),
    recommendation: z.string(),
    title: z.string(),
});
export type RunResultExtrauxeachFeedback = {
    priority: typeof priorityEnum;
    issue: string;
    recommendation: string;
    title: string;
};

export type RunResultExtra = {
    run_result_id?: number;
    extra: {
        ux: RunResultExtrauxeachFeedback[];
    };
};

export const getActionLabel = (action: WebAgentActionName, capitalizeFirst?: boolean): string => {
    switch (action) {
        case 'leftClick':
            return capitalizeFirst ? 'Left click' : 'left click';
        case 'typeText':
            return capitalizeFirst ? 'Type text' : 'type text';
        case 'selectOption':
            return capitalizeFirst ? 'Select option' : 'select option';
        case 'hover':
            return capitalizeFirst ? 'Hover' : 'hover';
        case 'pressEnter':
            return capitalizeFirst ? 'Press enter' : 'press enter';
        case 'extractSelector':
            return capitalizeFirst ? 'Extract selector' : 'extract selector';
    }
};
